import React, { useState, useEffect } from 'react'
import { BrowserRouter, useSearchParams } from 'react-router-dom'
import { TranslationProvider } from '../../shared/TranslationProvider'
import { useTranslation } from '~/hooks/useTranslation'
import { Calendar } from '../OutplacementBenefits/Calendar'
import { getApi } from '~/util/api'
import { Typography, Tabs } from '~/components/_atoms'
import { Stack, Page } from '~/components/_layouts'
import Spinner from '~/components/shared/Spinner'
import translations from '../locales.json'
import WebinarListItem from './WebinarListItem'
import { useMediaQueryScreen } from '~/hooks/useMediaQuery'

const UpcomingEvents = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const [isLoading, setIsLoading] = useState(false)
  const [calendarEvents, setCalendarEvents] = useState({ events: [] })
  const isLargeScreen = useMediaQueryScreen('lg')
  const [currentMode, setCurrentMode] = useState(() => {
    const modeFromParams = searchParams.get('mode')
    if (window.innerWidth >= 1024) {
      return modeFromParams || 'calendar'
    }
    return modeFromParams || 'list'
  })
  const selectedEventId = searchParams.get('event_id')
  const listRef = React.useRef(null)

  // Update mode when screen size changes, but respect the query parameter
  useEffect(() => {
    const modeFromParams = searchParams.get('mode')
    if (isLargeScreen) {
      setCurrentMode(modeFromParams || 'calendar')
    } else {
      setCurrentMode(modeFromParams || 'list')
    }
  }, [isLargeScreen, searchParams])

  useEffect(() => {
    setIsLoading(true)
    getApi()
      .get('/coach_services/upcoming_webinars')
      .then((response) => {
        if (response.status == 200) {
          setCalendarEvents(response.data)
          return
        } else {
          throw new Error('json request failed')
        }
      })
      .finally(() => {
        setIsLoading(false)
      })
      .catch((error) => {
        console.log('json request failed', error)
      })
  }, [setCalendarEvents])

  // Scroll to selected item when list view is active and data is loaded
  useEffect(() => {
    if (
      !isLoading &&
      currentMode === 'list' &&
      selectedEventId &&
      listRef.current
    ) {
      const selectedElement = listRef.current.querySelector(
        `[data-event-id="${selectedEventId}"]`,
      )
      if (selectedElement) {
        selectedElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }, [isLoading, currentMode, selectedEventId])

  return (
    <Page
      title={t('title')}
      header={<Typography>{t('description')}</Typography>}
      variant="coach-services"
    >
      <Stack spacing={3}>
        {isLoading ? (
          <div className="flex items-center justify-center">
            <Spinner show size={9} />
          </div>
        ) : (
          <>
            {isLargeScreen && (
              <Tabs
                items={[
                  { key: 'calendar', label: 'Calendar', icon: 'calendar' },
                  { key: 'list', label: 'List', icon: 'list' },
                ]}
                selectedItem={currentMode}
                onSelect={setCurrentMode}
                variant="secondary"
              />
            )}
            <div className="mt-4">
              {currentMode === 'calendar' && isLargeScreen && (
                <div className="relative">
                  <Calendar
                    calendar_events={calendarEvents.events}
                    homePageDisplay={false}
                    opRedesign={true}
                    coachDashboardDisplay={false}
                  />
                </div>
              )}
              {currentMode === 'list' && (
                <div ref={listRef}>
                  <Stack spacing={6}>
                    {calendarEvents.events.map((event) => (
                      <WebinarListItem
                        key={`event_${event.id}`}
                        webinar={event}
                        currentUser={calendarEvents.currentUser}
                        selected={selectedEventId === event.id.toString()}
                        data-event-id={event.id}
                      />
                    ))}
                  </Stack>
                </div>
              )}
            </div>
          </>
        )}
      </Stack>
    </Page>
  )
}

export default function UpcomingEventsWithProviders({ locale, ...props }) {
  return (
    <TranslationProvider
      locale={locale}
      translations={translations}
      scope="javascript.components.coach_services.UpcomingEvents"
    >
      <BrowserRouter>
        <UpcomingEvents {...props} />
      </BrowserRouter>
    </TranslationProvider>
  )
}
